/*
 * @Author: Ryan
 * @Date: 2026-01-14
 * @Description: Why choose FalcoCut AI Video Generator section with 6 feature cards
 */
import React from 'react';

interface FeatureCard {
  icon: string;
  title: string;
  description: string;
}

const features: FeatureCard[] = [
  {
    icon: '⚡️',
    title: 'Fast Video Generation',
    description:
      'Ideas turn into videos quickly, making it simple to test variations and explore new styles. Fast results keep creative work moving.',
  },
  {
    icon: '🧩',
    title: 'Multi-Mode Inputs',
    description:
      'Start a video from text, images, characters, or a mix of them. More input options make it easier to shape ideas in a natural way.',
  },
  {
    icon: '✨',
    title: 'Consistent and Clear Results',
    description:
      'Each video comes out steady, smooth, and sharp. Reliable quality makes the output ready for work, school, or social platforms.',
  },
  {
    icon: '🔒',
    title: 'Safe and Secure',
    description:
      'Strong safety and privacy standards protect every asset. A stable, trusted environment keeps creative work worry-free.',
  },
  {
    icon: '💡',
    title: 'Simple for Everyone',
    description:
      'A clean, intuitive interface removes the need for editing skills or training. Creating a polished video takes only a few minutes.',
  },
  {
    icon: '💰',
    title: 'Works for Many Projects',
    description:
      'Short clips, explainers, ads, and personal stories all fit the same workflow. One tool supports a wide range of creative needs.',
  },
];

const FeatureCardItem: React.FC<FeatureCard> = ({ icon, title, description }) => {
  return (
    <div className="bg-white rounded-2xl p-6 pad:p-8 shadow-sm border border-gray-100">
      <div className="text-3xl pad:text-4xl mb-3 pad:mb-4">{icon}</div>
      <h3 className="text-lg pad:text-xl font-semibold text-gray-900 mb-2 pad:mb-3">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

const WhyChooseSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Why Choose FalcoCut for AI Video Creation
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-4xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          FalcoCut gives you a reliable, creator-friendly workflow that turns ideas into
          polished videos with minimal effort. From fast generation to consistent visual
          quality, every part of the platform is designed to help you produce content
          confidently.
        </p>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 pad:grid-cols-2 gap-4 pad:gap-6">
          {features.map((feature, index) => (
            <FeatureCardItem
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
